import { adminFetch } from "./_client";
import type { InsuranceProvider, InsurancePackage, InsurancePricing, HourlyDiscount, CarValueRange, BulkImportResponse, BulkImportCommitResponse } from "./types";


export function fetchInsuranceProviders(token: string) {
  return adminFetch<{ providers: InsuranceProvider[] }>("/admin/insurance/providers", token);
}


export function createInsuranceProvider(token: string, data: { name: string; contact?: string; regions?: string }) {
  return adminFetch<InsuranceProvider>("/admin/insurance/providers", token, { method: "POST", body: JSON.stringify(data) });
}


export function updateInsuranceProvider(token: string, id: number, data: Partial<InsuranceProvider>) {
  return adminFetch<{ success: boolean }>(`/admin/insurance/providers/${id}`, token, { method: "PUT", body: JSON.stringify(data) });
}


export function deleteInsuranceProvider(token: string, id: number) {
  return adminFetch<{ success: boolean }>(`/admin/insurance/providers/${id}`, token, { method: "DELETE" });
}


export function fetchInsurancePackages(token: string) {
  return adminFetch<{ packages: InsurancePackage[] }>("/admin/insurance/packages", token);
}


export function createInsurancePackage(token: string, data: { name: string; description?: string; coverage_liability?: boolean; coverage_physical?: boolean; coverage_roadside?: boolean }) {
  return adminFetch<InsurancePackage>("/admin/insurance/packages", token, { method: "POST", body: JSON.stringify(data) });
}


export function updateInsurancePackage(token: string, id: number, data: Partial<InsurancePackage>) {
  return adminFetch<{ success: boolean }>(`/admin/insurance/packages/${id}`, token, { method: "PUT", body: JSON.stringify(data) });
}


export function deleteInsurancePackage(token: string, id: number) {
  return adminFetch<{ success: boolean }>(`/admin/insurance/packages/${id}`, token, { method: "DELETE" });
}


export function fetchInsurancePricing(token: string) {
  return adminFetch<{ pricing: InsurancePricing[] }>("/admin/insurance/pricing", token);
}


export function createInsurancePricing(token: string, data: { insurance_package_id: number; car_value_range_id?: number; rental_type?: string; daily_rate: number }) {
  return adminFetch<InsurancePricing>("/admin/insurance/pricing", token, { method: "POST", body: JSON.stringify(data) });
}


export function updateInsurancePricing(token: string, id: number, data: Partial<InsurancePricing>) {
  return adminFetch<{ success: boolean }>(`/admin/insurance/pricing/${id}`, token, { method: "PUT", body: JSON.stringify(data) });
}


export function deleteInsurancePricing(token: string, id: number) {
  return adminFetch<{ success: boolean }>(`/admin/insurance/pricing/${id}`, token, { method: "DELETE" });
}


export function fetchHourlyDiscounts(token: string) {
  return adminFetch<{ discounts: HourlyDiscount[] }>("/admin/insurance/hourly-discounts", token);
}


export function createHourlyDiscount(token: string, data: { hours: number; discount_percentage: number; insurance_package_id?: number }) {
  return adminFetch<HourlyDiscount>("/admin/insurance/hourly-discounts", token, { method: "POST", body: JSON.stringify(data) });
}


export function updateHourlyDiscount(token: string, id: number, data: Partial<HourlyDiscount>) {
  return adminFetch<{ success: boolean }>(`/admin/insurance/hourly-discounts/${id}`, token, { method: "PUT", body: JSON.stringify(data) });
}


export function deleteHourlyDiscount(token: string, id: number) {
  return adminFetch<{ success: boolean }>(`/admin/insurance/hourly-discounts/${id}`, token, { method: "DELETE" });
}


export function fetchCarValueRanges(token: string) {
  return adminFetch<{ ranges: CarValueRange[] }>("/admin/insurance/car-value-ranges", token);
}


export function createCarValueRange(token: string, data: { min_value: number; max_value: number; label?: string }) {
  return adminFetch<CarValueRange>("/admin/insurance/car-value-ranges", token, { method: "POST", body: JSON.stringify(data) });
}


export function updateCarValueRange(token: string, id: number, data: Partial<CarValueRange>) {
  return adminFetch<{ success: boolean }>(`/admin/insurance/car-value-ranges/${id}`, token, { method: "PUT", body: JSON.stringify(data) });
}


export function deleteCarValueRange(token: string, id: number) {
  return adminFetch<{ success: boolean }>(`/admin/insurance/car-value-ranges/${id}`, token, { method: "DELETE" });
}


export function updateCarInsuranceExemptions(token: string, carId: number, data: { insurance_exempt_liability?: boolean; insurance_exempt_physical?: boolean }) {
  return adminFetch<{ success: boolean }>(`/admin/listings/${carId}/insurance-exemptions`, token, { method: "PUT", body: JSON.stringify(data) });
}


export function fetchTripInsuranceExemptions(token: string, tripId: number) {
  return adminFetch<{ insurance_exempt_liability: boolean; insurance_exempt_physical: boolean; suggestion_id: number | null }>(`/admin/trips/${tripId}/insurance-exemptions`, token);
}


export function updateTripInsuranceExemptions(token: string, tripId: number, data: { insurance_exempt_liability?: boolean; insurance_exempt_physical?: boolean }) {
  return adminFetch<{ success: boolean }>(`/admin/trips/${tripId}/insurance-exemptions`, token, { method: "PUT", body: JSON.stringify(data) });
}


export function uploadBulkInsuranceCSV(token: string, csvContent: string) {
  return adminFetch<BulkImportResponse>("/admin/insurance/bulk-import", token, {
    method: "POST",
    body: JSON.stringify({ csvContent }),
  });
}


export function commitBulkInsuranceCSV(token: string, csvContent: string) {
  return adminFetch<BulkImportCommitResponse>("/admin/insurance/bulk-import/commit", token, {
    method: "POST",
    body: JSON.stringify({ csvContent, confirm: true }),
  });
}
